import { MessagePayload } from "@/shared/webworker";

import { GeoMessagePath } from "../geoTypes";

export const SOURCE = "MapDataService";

export enum MapDataCommand {
  Init = "init",
  LoadPathData = "load-path-data",
  DisposePath = "dispose-path",
}

export enum MapDataCommandResponse {
  /** Transferable coordinate data for a path segment */
  PathCoordinates = "path-coordinates",
  /** All data for a path has been sent */
  PathDataComplete = "path-data-complete",
}

export interface LoadPathDataPayload {
  pathId: string;
  topicId: string;
  latMessagePath: GeoMessagePath;
  lonMessagePath: GeoMessagePath;
  latMessagePathId: string;
  lonMessagePathId: string;
}

/**
 * Transferable coordinate buffers sent from worker to main thread.
 * These are transferred (not cloned) for zero-copy efficiency.
 */
export interface PathCoordinatesPayload {
  pathId: string;
  /** Latitude values (Float64Array transferred) */
  latitudes: Float64Array;
  /** Longitude values (Float64Array transferred) */
  longitudes: Float64Array;
  /** Log timestamps in nanoseconds (BigUint64Array transferred) */
  logTimes: BigUint64Array;
  /** Offset into the full path (for incremental streaming) */
  startIndex: number;
}

export interface MapDataCommandPayloadMap {
  [MapDataCommand.Init]: undefined;
  [MapDataCommand.LoadPathData]: LoadPathDataPayload;
  [MapDataCommand.DisposePath]: { pathId: string; topicId: string };
}

export interface MapDataCommandResponsePayloadMap {
  [MapDataCommandResponse.PathCoordinates]: PathCoordinatesPayload;
  [MapDataCommandResponse.PathDataComplete]: {
    pathId: string;
    totalCount: number;
  };
}

export function isInitCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<MapDataCommand, MapDataCommandPayloadMap[MapDataCommand.Init]>
> {
  return event.data.type === MapDataCommand.Init;
}

export function isLoadPathDataCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommand,
    MapDataCommandPayloadMap[MapDataCommand.LoadPathData]
  >
> {
  return event.data.type === MapDataCommand.LoadPathData;
}

export function isDisposePathCommand(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommand,
    MapDataCommandPayloadMap[MapDataCommand.DisposePath]
  >
> {
  return event.data.type === MapDataCommand.DisposePath;
}

export function isPathCoordinatesResponse(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommandResponse,
    MapDataCommandResponsePayloadMap[MapDataCommandResponse.PathCoordinates]
  >
> {
  return event.data.type === MapDataCommandResponse.PathCoordinates;
}

export function isPathDataCompleteResponse(
  event: MessageEvent<MessagePayload>,
): event is MessageEvent<
  MessagePayload<
    MapDataCommandResponse,
    MapDataCommandResponsePayloadMap[MapDataCommandResponse.PathDataComplete]
  >
> {
  return event.data.type === MapDataCommandResponse.PathDataComplete;
}
